import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import { createClaimLink, updateCommerceContact, type PartnerCommerce } from '@services/partnerService';
import { TextInputV4 } from '@components/v4/Input/Input';
import { V4 } from '@components/v4/tokens';
import { partnerEvent } from '../analytics';
import { usePartnerAuth } from '../PartnerAuthContext';
import { Badge, Button, C, Card, ErrorNote, Field, InfoRow, Muted, PhoneField, SectionTitle, Title } from '../ui';
import { isValidLocal, localDigits, toStoredPhone } from '../phone';
import { fmtDate, useCommerces } from '../useCommerces';
import { copyTextToClipboard } from '../clipboard';

// Texto listo para que el partner lo pegue en SU WhatsApp y se lo mande al dueño, con el ENLACE
// de pago real (mismo /pre-registro/{token} de siempre, regenerado — ver claimLink.js/open.js: un
// comercio ya reclamado con un pago pendiente puede volver a abrir ese enlace y retoma directo la
// selección de plan + pago, sin crear su cuenta de nuevo).
const buildAccessMessage = (c: PartnerCommerce, link: string) =>
  [
    `Hola ${c.owner_nombre || ''}, aquí está tu enlace para terminar de contratar tu plan ${c.tipo_plan || ''} en Plick:`,
    '',
    link,
    '',
    'Ábrelo y termina tu pago — no hace falta crear tu cuenta de nuevo, ya está lista.',
    '',
    'Cualquier duda, aquí estoy para ayudarte 🙌',
  ].join('\n');

// Mismo patrón que buildAccessMessage, para el paso "Invitar al dueño" de un draft NUEVO (aún sin
// cuenta): confirma WhatsApp + crea usuario/contraseña + elige plan, no "termina un pago pendiente".
const buildInviteMessage = (c: PartnerCommerce, link: string) =>
  [
    `Hola ${c.owner_nombre || ''}, tu comercio ${c.negocio_nombre || ''} ya está listo en Plick 🎉`,
    '',
    `Actívalo aquí (te toma menos de 1 minuto): ${link}`,
    '',
    'Solo confirma tu WhatsApp y crea tu usuario y contraseña. Al final eliges tu plan (puedes empezar gratis).',
    '',
    'Cualquier duda, aquí estoy para ayudarte 🙌',
  ].join('\n');

// --- Guía de alta paso a paso (Problema 3) --------------------------------------------------
// El dueño reportó que 4 acciones al mismo nivel obligan al partner a "adivinar" el orden del
// funnel. Se reemplaza por un checklist ordenado con UN solo primario = el siguiente paso.
type StepStatus = 'done' | 'recommended' | 'pending';

const StepBullet = ({ n, status }: { n: number; status: StepStatus }) => {
  const base = {
    width: 26,
    height: 26,
    borderRadius: 9999,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1,
  } as const;
  if (status === 'done') {
    return (
      <span style={{ ...base, background: V4.success, color: V4.white }} aria-hidden>
        ✓
      </span>
    );
  }
  if (status === 'recommended') {
    return (
      <span style={{ ...base, background: C.secondary, color: V4.white }} aria-hidden>
        {n}
      </span>
    );
  }
  return (
    <span style={{ ...base, background: V4.white, border: `1.5px solid ${C.cardBorder}`, color: C.muted }} aria-hidden>
      {n}
    </span>
  );
};

const StepPill = ({ tone }: { tone: 'done' | 'recommended' }) => {
  const done = tone === 'done';
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        padding: '2px 8px',
        borderRadius: 9999,
        whiteSpace: 'nowrap',
        color: done ? V4.successDark : V4.secondaryDark,
        background: done ? V4.successFaded : V4.secondaryFaded,
      }}
    >
      {done ? 'Listo' : 'Siguiente'}
    </span>
  );
};

const StepRow = ({
  n,
  status,
  title,
  desc,
  action,
  first,
}: {
  n: number;
  status: StepStatus;
  title: string;
  desc: string;
  action: ReactNode;
  first?: boolean;
}) => (
  <div
    style={{
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
      padding: '14px 0',
      borderTop: first ? 'none' : `1px solid ${C.cardBorder}`,
    }}
  >
    <StepBullet n={n} status={status} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: C.primary, opacity: status === 'pending' ? 0.85 : 1 }}>
          {title}
        </span>
        {status !== 'pending' && <StepPill tone={status === 'done' ? 'done' : 'recommended'} />}
      </div>
      <div style={{ fontSize: 12.5, color: C.muted, margin: '2px 0 10px', lineHeight: 1.4, overflowWrap: 'anywhere' }}>
        {desc}
      </div>
      {action}
    </div>
  </div>
);

// --- Pago del plan pendiente (Problema: claimed_at != null NO prueba que el pago se confirmó) --
// Componente propio (no inline en CommerceDetail) para mantener la complejidad del padre bajo
// control y aislar el estado de copiado/fallback de todo lo demás de la página.
//
// El enlace de pago ES el mismo enlace de pre-registro (/pre-registro/{token}) — claimLink.js
// permite regenerarlo para un comercio ya reclamado mientras tenga un pago pendiente, y
// claim/open.js detecta la identidad ya creada y salta directo al paso de plan (sin OTP). NO hay
// un "Mi cuenta" ni un login normal involucrado — todo pasa por ese mismo enlace.
const PendingPaymentCard = ({
  c,
  link,
  linkError,
  genLoading,
  onGenerateLink,
  copied,
  copyError,
  onCopy,
}: {
  c: PartnerCommerce;
  link: string | null;
  linkError: string | null;
  genLoading: boolean;
  onGenerateLink: () => void;
  copied: boolean;
  copyError: boolean;
  onCopy: () => void;
}) => {
  const [linkCopied, setLinkCopied] = useState(false);
  const copyLink = () => {
    if (!link) return;
    copyTextToClipboard(link, (ok) => {
      if (ok) {
        setLinkCopied(true);
        window.setTimeout(() => setLinkCopied(false), 2500);
      }
    });
  };

  return (
    <Card>
      <SectionTitle>Pago del plan pendiente</SectionTitle>
      <Muted style={{ marginTop: 0, marginBottom: 16 }}>
        El dueño ya creó su cuenta, pero el pago de <strong>{c.tipo_plan || 'su plan'}</strong> no se completó (tarjeta
        declinada, 3D Secure sin resolver, o abandonó el pago). El comercio sigue operando en FREEMIUM mientras tanto.
      </Muted>

      <p
        style={{
          margin: '0 0 10px',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: C.muted,
        }}
      >
        Cómo ayudarlo a terminar
      </p>

      <StepRow
        first
        n={1}
        status={link ? 'done' : 'recommended'}
        title="Genera el enlace de pago"
        desc="Es el mismo enlace de pre-registro: al abrirlo, retoma directo la selección de plan y el pago — no vuelve a crear su cuenta."
        action={
          <Button variant="secondary" onClick={onGenerateLink} disabled={genLoading}>
            {genLoading ? 'Generando...' : link ? 'Regenerar enlace' : 'Generar enlace de pago'}
          </Button>
        }
      />
      {linkError && <ErrorNote>{linkError}</ErrorNote>}
      {link && (
        <div style={{ margin: '0 0 16px 38px' }}>
          <TextInputV4
            value={link}
            onChange={() => {}}
            readOnly
            onFocus={(e) => e.currentTarget.select()}
            aria-label="Enlace de pago"
            suffix={
              <button
                type="button"
                onClick={copyLink}
                aria-label={linkCopied ? 'Enlace copiado' : 'Copiar enlace'}
                title={linkCopied ? 'Enlace copiado' : 'Copiar enlace'}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  cursor: 'pointer',
                  color: linkCopied ? V4.success : C.secondary,
                }}
              >
                {linkCopied ? <IconCheck size={18} /> : <IconCopy size={18} />}
              </button>
            }
          />
        </div>
      )}

      <StepRow
        n={2}
        status={link ? 'recommended' : 'pending'}
        title="Envíaselo por WhatsApp"
        desc={c.owner_telefono ? `Su número: ${c.owner_telefono}` : 'Número no disponible — usa el correo.'}
        action={
          <Button variant="secondary" onClick={onCopy} disabled={!link}>
            {copied ? 'Mensaje copiado ✓' : 'Copiar mensaje para el dueño'}
          </Button>
        }
      />

      {copyError && link && (
        <div style={{ marginTop: 14 }}>
          <ErrorNote>No se pudo copiar automáticamente. Selecciona y copia el texto manualmente:</ErrorNote>
          <textarea
            readOnly
            value={buildAccessMessage(c, link)}
            onFocus={(e) => e.currentTarget.select()}
            rows={6}
            style={{
              width: '100%',
              marginTop: 8,
              padding: 10,
              fontFamily: 'Poppins, sans-serif',
              fontSize: 12.5,
              borderRadius: 8,
              border: `1px solid ${C.cardBorder}`,
              color: C.primary,
              resize: 'vertical',
            }}
          />
        </div>
      )}
    </Card>
  );
};

const CommerceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { token } = usePartnerAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { commerces, loading, refresh } = useCommerces();
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [genLoading, setGenLoading] = useState(false);
  const [accessMessageCopied, setAccessMessageCopied] = useState(false);
  const [accessMessageError, setAccessMessageError] = useState(false);
  const [inviteMessageCopied, setInviteMessageCopied] = useState(false);
  const [inviteMessageError, setInviteMessageError] = useState(false);
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false);

  const c = commerces.find((x) => x.id_comercio === id);
  const claimed = c?.estado === 'reclamado';
  // claimed_at solo prueba que el dueño creó su cuenta — un pago de plan con tarjeta puede
  // seguir sin confirmarse (Stripe deja la suscripción 'incomplete' hasta el 3DS). No es lo
  // mismo que un alta realmente terminada.
  const pending = claimed && Boolean(c?.plan_pending);
  // Aviso de éxito al volver del wizard "Preparar comercio" (Finalizar → ?prepared=1).
  const prepared = searchParams.get('prepared') === '1';
  // El banner verde de abajo lee `showPreparedBanner` (NO `prepared` directo): el efecto de más
  // abajo borra `?prepared=1` de la URL de inmediato para no dejarlo pegado tras un refresh, lo que
  // hacía que `prepared` volviera a `false` en el siguiente render y el banner desapareciera casi
  // instantáneo (~un paint). Al capturar el valor inicial en un `useState` (solo se lee UNA vez, al
  // montar), el banner sobrevive esa limpieza de URL durante toda la vida de este montaje de la
  // página, y desaparece naturalmente si el partner navega fuera y vuelve (nuevo montaje, sin
  // `?prepared=1` en la URL).
  const [showPreparedBanner] = useState(prepared);
  // Bug real reportado: el paso 1 "Preparar comercio" de la Guía de alta NUNCA se marcaba "Listo"
  // — `prepared` viene del query param, que el efecto de abajo borra de la URL de inmediato, así
  // que la señal se perdía en el mismo render. Se persiste en sessionStorage (scoped por comercio)
  // para que sobreviva esa limpieza de URL y también un ir-y-volver dentro de la MISMA sesión del
  // navegador (backend no expone aún una fecha real de "comercio preparado" — ver TODO más abajo).
  const wizardPreparedKey = id ? `partner_wizard_prepared_${id}` : null;
  const [wizardPrepared, setWizardPrepared] = useState(
    () => wizardPreparedKey !== null && sessionStorage.getItem(wizardPreparedKey) === '1',
  );

  const copyAccessMessage = () => {
    if (!c || !link) return;
    copyTextToClipboard(buildAccessMessage(c, link), (ok) => {
      setAccessMessageError(!ok);
      if (ok) {
        setAccessMessageCopied(true);
        window.setTimeout(() => setAccessMessageCopied(false), 2500);
      }
    });
  };

  const copyInviteMessage = () => {
    if (!c || !link) return;
    copyTextToClipboard(buildInviteMessage(c, link), (ok) => {
      setInviteMessageError(!ok);
      if (ok) {
        setInviteMessageCopied(true);
        window.setTimeout(() => setInviteMessageCopied(false), 2500);
      }
    });
  };

  // Copia SOLO el link crudo (distinto de copyInviteMessage, que copia el mensaje completo) — para
  // el boton con icono de clipboard dentro del input del enlace.
  const copyInviteLink = () => {
    if (!link) return;
    copyTextToClipboard(link, (ok) => {
      if (ok) {
        setInviteLinkCopied(true);
        window.setTimeout(() => setInviteLinkCopied(false), 2500);
      }
    });
  };

  useEffect(() => {
    if (prepared) {
      if (wizardPreparedKey) sessionStorage.setItem(wizardPreparedKey, '1');
      setWizardPrepared(true);
      const next = new URLSearchParams(searchParams);
      next.delete('prepared');
      setSearchParams(next, { replace: true });
    }
  }, [prepared, searchParams, setSearchParams, wizardPreparedKey]);

  // Edicion de contacto (solo drafts). form.telefono guarda solo los 10 digitos nacionales.
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);

  const openEdit = () => {
    setSaveError(null);
    setPhoneError(null);
    setSavedOk(false);
    setForm({
      nombre: c?.owner_nombre || '',
      email: c?.owner_correo || '',
      telefono: localDigits(c?.owner_telefono || ''),
    });
    setEditing(true);
  };

  // Auto-abre el editor si se llego con ?edit=1 (accion rapida desde la lista).
  useEffect(() => {
    if (!loading && c && !claimed && searchParams.get('edit') === '1' && !editing) {
      openEdit();
      // React Router trata `searchParams` como inmutable: clona antes de mutar para no contaminar
      // el objeto memoizado del hook y garantizar que detecte el cambio de URL.
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
  }, [loading, c, claimed, searchParams]);

  const onClaimLink = async () => {
    if (!token || !id) return;
    setError(null);
    setGenLoading(true);
    try {
      const res = await createClaimLink(token, id);
      setLink(`${window.location.origin}/pre-registro/${res.token}`);
      partnerEvent('partner_claim_link_generated');
    } catch {
      setError('No se pudo generar el enlace de activación.');
    } finally {
      setGenLoading(false);
    }
  };

  const onSaveContact = async () => {
    if (!token || !id) return;
    setSaveError(null);
    setSavedOk(false);

    // El telefono es el destino del OTP del claim: si lo capturan, debe ser un MX valido de 10 digitos.
    if (form.telefono.trim() && !isValidLocal(form.telefono)) {
      setPhoneError('Ingresa un WhatsApp valido de 10 digitos.');
      return;
    }
    setPhoneError(null);

    setSaving(true);
    try {
      await updateCommerceContact(token, id, {
        nombre: form.nombre.trim() || undefined,
        email: form.email.trim() || undefined,
        telefono: form.telefono.trim() ? toStoredPhone(form.telefono) : undefined,
      });
      await refresh();
      setEditing(false);
      setSavedOk(true);
      partnerEvent('partner_contact_updated');
    } catch {
      setSaveError('No se pudo guardar el contacto. Revisa los datos e intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Muted>Cargando...</Muted>;
  if (!c) {
    return (
      <>
        <Title>Comercio no encontrado</Title>
        <Button variant="secondary" onClick={() => navigate('/partner/comercios')}>
          Volver a comercios
        </Button>
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => navigate('/partner/comercios')}
        style={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: 13,
          fontWeight: 600,
          color: C.secondary,
          background: 'transparent',
          border: 'none',
          padding: 0,
          marginBottom: 14,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <span aria-hidden style={{ fontSize: 15, lineHeight: 1 }}>
          ‹
        </span>
        Comercios
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap', minWidth: 0 }}>
        <Title>{c.negocio_nombre || 'Comercio sin nombre'}</Title>
        <Badge tone={pending ? 'pending' : claimed ? 'claimed' : 'draft'}>
          {pending ? 'Pago pendiente' : claimed ? 'Activado' : 'Por activar'}
        </Badge>
      </div>

      {showPreparedBanner && (
        <div
          style={{
            background: '#D1FFD0',
            border: '1px solid #04AF00',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 16,
            fontSize: 13,
            color: '#037E00',
          }}
        >
          Comercio preparado. Comparte el enlace de activación con el dueño para que active su cuenta.
        </div>
      )}

      <Card>
        <SectionTitle>Información</SectionTitle>
        <InfoRow first label="Negocio" value={c.negocio_nombre || ''} />
        <InfoRow label="Dueño" value={c.owner_nombre || ''} />
        <InfoRow label="WhatsApp" value={c.owner_telefono || ''} />
        <InfoRow label="Email" value={c.owner_correo || ''} />
        <InfoRow label="Plan" value={c.tipo_plan || 'FREEMIUM'} />
        <InfoRow
          label="Estado"
          value={
            pending
              ? `Activado el ${fmtDate(c.claimed_at)} · pago del plan pendiente`
              : claimed
                ? `Activado el ${fmtDate(c.claimed_at)}`
                : 'Por activar'
          }
        />
        {claimed && <InfoRow label="Usuario" value={c.owner_usuario || 'No disponible'} />}
        <InfoRow label="Creado" value={fmtDate(c.created_at)} />
        {savedOk && <Muted style={{ color: C.success }}>Contacto actualizado correctamente.</Muted>}
      </Card>

      {pending && (
        <PendingPaymentCard
          c={c}
          link={link}
          linkError={error}
          genLoading={genLoading}
          onGenerateLink={onClaimLink}
          copied={accessMessageCopied}
          copyError={accessMessageError}
          onCopy={copyAccessMessage}
        />
      )}

      {!claimed && editing && (
        <Card>
          <SectionTitle>Editar contacto del dueño</SectionTitle>
          <Muted style={{ marginTop: 0, marginBottom: 16 }}>
            Corrige el contacto del dueño. El código de verificación se envía a este WhatsApp, así que un teléfono
            incorrecto impide que el dueño active su cuenta.
          </Muted>
          <Field
            label="Nombre del dueño"
            value={form.nombre}
            onChange={(val) => setForm((f) => ({ ...f, nombre: val }))}
            placeholder="Nombre y apellido"
          />
          <PhoneField
            label="WhatsApp"
            value={form.telefono}
            onChange={(v) => {
              setForm((f) => ({ ...f, telefono: v }));
              if (phoneError) setPhoneError(null);
            }}
            error={phoneError}
          />
          <Field
            label="Email"
            value={form.email}
            onChange={(val) => setForm((f) => ({ ...f, email: val }))}
            placeholder="correo@ejemplo.com"
            type="email"
          />
          {saveError && <ErrorNote>{saveError}</ErrorNote>}
          <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
            <Button onClick={onSaveContact} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar contacto'}
            </Button>
            <Button variant="secondary" onClick={() => setEditing(false)} disabled={saving}>
              Cancelar
            </Button>
          </div>
        </Card>
      )}

      {!claimed &&
        (() => {
          // Derivación del estado de los pasos.
          // Este card solo se renderiza para drafts (!claimed): el comercio aún NO está activado,
          // así que el paso final (dueño activa su cuenta) nunca se marca "hecho" aquí — cuando se
          // activa, `claimed` pasa a true y todo este bloque desaparece.
          //
          // Señales REALES disponibles hoy en PartnerCommerce: `estado`/`claimed_at` +
          // `menu_items_count` (el backend cuenta los productos del catálogo del comercio, 0 si no
          // hay menú). El resto del progreso ("preparado"/"invitación enviada") aún NO tiene flag
          // persistente en backend que sobreviva entre SESIONES de navegador distintas, así que se
          // infiere de forma conservadora, persistida en sessionStorage (sobrevive navegar
          // ida-y-vuelta en la MISMA sesión, no un cierre de pestaña):
          //   - `wizardPrepared` (?prepared=1 de finishWizard, ver efecto arriba) = el partner completó
          //     el wizard de 7 pasos completo (incluye el paso de menú) → paso 1 hecho.
          //   - `link` (state local) = el partner generó el enlace en ESTA sesión → paso 3 hecho.
          // El paso 2 "Cargar menú" usa EXCLUSIVAMENTE el estado real del catálogo (`menu_items_count`):
          // así se marca listo aunque el menú se haya cargado en otra sesión/dispositivo, y NUNCA se
          // marca listo si el catálogo está vacío (el menú es skippable en el wizard, así que preparar
          // el comercio no implica haber cargado menú).
          const linkGenerated = link !== null;
          const hasMenu = (c.menu_items_count ?? 0) > 0;
          // El paso 1 "Preparar comercio" se deriva de la señal REAL del backend (`is_prepared`,
          // contrato con `listByPartner`), con fallback al flag de sesión `wizardPrepared` para no
          // regresar el estado si el backend aún no expone el campo o mientras el listado refresca.
          const prepareStatus: StepStatus = c.is_prepared || wizardPrepared ? 'done' : 'recommended';
          // El paso "Cargar menú" se marca listo SOLO con el estado real del catálogo
          // (`menu_items_count`). No hay fallback a `wizardPrepared`: el menú es skippable dentro
          // del wizard (ver ConfigureCommerce MenuStep `skippable`), así que "preparó el comercio"
          // NO implica que cargó menú. Usar `wizardPrepared` como fallback marcaría "Listo" un
          // catálogo vacío — engañoso y en contra del commit que basó este paso en el conteo real.
          const menuStatus: StepStatus = hasMenu ? 'done' : 'pending';
          const inviteStatus: StepStatus = linkGenerated ? 'done' : wizardPrepared ? 'recommended' : 'pending';

          return (
            <Card>
              <SectionTitle>Guía de alta</SectionTitle>
              <Muted style={{ marginTop: 0, marginBottom: 6 }}>
                {linkGenerated
                  ? 'Enlace de activación generado. Compártelo con el dueño para que active su cuenta.'
                  : wizardPrepared
                    ? 'Comercio preparado ✓ Ahora invita al dueño para que active su cuenta.'
                    : 'Sigue estos 3 pasos en orden para dejar el comercio listo. Empieza por preparar el comercio; entre más prepares, más rápido será el alta del dueño (solo confirmar).'}
              </Muted>

              <StepRow
                first
                n={1}
                status={prepareStatus}
                title="Preparar comercio"
                desc="Datos, dirección, entrega, horarios y menú en un solo flujo."
                action={
                  <Button
                    variant={prepareStatus === 'recommended' ? 'primary' : 'secondary'}
                    onClick={() => navigate(`/partner/draft/${c.id_comercio}/configurar`)}
                  >
                    Preparar comercio
                  </Button>
                }
              />
              <StepRow
                n={2}
                status={menuStatus}
                title="Cargar menú"
                desc="Agrega productos y categorías (también disponible dentro de Preparar comercio)."
                action={
                  <Button
                    variant="secondary"
                    onClick={() => navigate(`/partner/draft/${c.id_comercio}/configurar?step=menu`)}
                  >
                    Cargar menú
                  </Button>
                }
              />
              <StepRow
                n={3}
                status={inviteStatus}
                title="Invitar al dueño"
                desc="Genera el enlace para que el dueño active su cuenta y tome el control del comercio."
                action={
                  <Button
                    variant={inviteStatus === 'recommended' ? 'primary' : 'secondary'}
                    onClick={onClaimLink}
                    disabled={genLoading}
                  >
                    {genLoading ? 'Generando...' : linkGenerated ? 'Regenerar enlace' : 'Invitar al dueño'}
                  </Button>
                }
              />

              {error && <ErrorNote>{error}</ErrorNote>}
              {link && (
                <div style={{ marginTop: 14 }}>
                  <Muted style={{ marginTop: 0, marginBottom: 6 }}>Comparte este enlace con el dueño:</Muted>
                  <TextInputV4
                    value={link}
                    onChange={() => {}}
                    readOnly
                    onFocus={(e) => e.currentTarget.select()}
                    aria-label="Enlace de activación"
                    suffix={
                      <button
                        type="button"
                        onClick={copyInviteLink}
                        aria-label={inviteLinkCopied ? 'Enlace copiado' : 'Copiar enlace'}
                        title={inviteLinkCopied ? 'Enlace copiado' : 'Copiar enlace'}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          margin: 0,
                          cursor: 'pointer',
                          color: inviteLinkCopied ? V4.success : C.secondary,
                        }}
                      >
                        {inviteLinkCopied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                      </button>
                    }
                  />

                  <Muted style={{ marginTop: 14, marginBottom: 6 }}>O envíale este mensaje ya redactado:</Muted>
                  <div
                    style={{
                      padding: 10,
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: 12.5,
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                      borderRadius: 8,
                      border: `1px solid ${C.cardBorder}`,
                      color: C.primary,
                      background: V4.white,
                    }}
                  >
                    {buildInviteMessage(c, link)}
                  </div>
                  <Button variant="secondary" onClick={copyInviteMessage} style={{ marginTop: 8 }}>
                    {inviteMessageCopied ? 'Mensaje copiado ✓' : 'Copiar mensaje'}
                  </Button>

                  {inviteMessageError && (
                    <div style={{ marginTop: 10 }}>
                      <ErrorNote>No se pudo copiar automáticamente. Selecciona y copia el texto de arriba.</ErrorNote>
                    </div>
                  )}
                </div>
              )}

              {/* Acción utilitaria (no es un paso del funnel): corregir el contacto del dueño. */}
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.cardBorder}` }}>
                <Muted style={{ marginTop: 0, marginBottom: 8 }}>
                  ¿Datos del dueño incorrectos? El código de verificación se envía a su WhatsApp.
                </Muted>
                <Button variant="ghost" onClick={openEdit} disabled={editing}>
                  Editar contacto
                </Button>
              </div>
            </Card>
          );
        })()}
    </>
  );
};

export default CommerceDetail;
